import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { AccountInitialValues, AccountSchema, ChangePasswordInitialValues, ChangePasswordSchema, ChangeEmailInitialValues, ChangeEmailSchema } from "./helper";
import { ClipLoader } from "react-spinners";
import { useDispatch, useSelector } from "react-redux";
import { useUpdateProfileMutation, useUpdatePasswordMutation, useChangeEmailMutation } from "../../redux/slices/profileApiSlice";
import { toast } from "react-toastify";
import { setCredentials } from "../../redux/slices/authSlice";
import { InputProfile } from "../../shared/components/profile"

const Account = () => {
  const [update, {isLoading}] = useUpdateProfileMutation();
  const [updatePassword, { passwordIsLoading }] = useUpdatePasswordMutation();
  const [updateEmail, { emailIsLoading }] = useChangeEmailMutation();
  const dispatch = useDispatch();
  const { userInfo, accessToken } = useSelector((state) => state?.auth);
  const { t } = useTranslation();

  return (
    <div>
      {/* PERSONAL INFOS */}
      <div className="mb-5 mx-auto flex flex-col bg-white shadow-default rounded-2xl">
        <div className="w-full p-6">
          <Formik
            initialValues={userInfo ? userInfo : AccountInitialValues}
            enableReinitialize={true}
            validationSchema={AccountSchema}
            onSubmit={async (values) => {
              try {
                const res = await update(values);

                if(res?.data?.message){
                  const updatedUserInfo = {
                    ...userInfo,
                    first_name: values.first_name,
                    last_name: values.last_name,
                    username: values.username
                  };

                  dispatch(setCredentials({
                    accessToken: accessToken,
                    userInfo: updatedUserInfo
                  }));
                  toast.success("Updated Successfully");
                }
              
              } catch (error) {
                toast.error(error?.data?.detail || "Something went wrong");
              }
            }}
          >
            {(props) => (
              <form onSubmit={props.handleSubmit}>
                <h2 className="mb-4">{t("Personal-infos", "Informations personnelles")}</h2>
                <div className="grid md:grid-cols-3 gap-x-11 gap-5">
                  <InputProfile
                    formikProps={props}
                    type="text"
                    text={t("enter-first-name", "Entrez votre prénom")}
                    name="first_name"
                    placeholder={t("enter-first-name", "Entrez votre prénom")}
                  />

                  <InputProfile
                    formikProps={props}
                    type="text"
                    text={t("enter-last-name", "Entrez votre nom de famille")}
                    name="last_name"
                    placeholder={t("enter-last-name", "Entrez votre nom de famille")}
                  />

                  <InputProfile
                    formikProps={props}
                    type="text"
                    text={t("enter-username", "Entrez votre nom d'utilisateur")}
                    name="username"
                    placeholder={t("enter-username", "Entrez votre nom d'utilisateur")}
                  />

                  <div className="col-span-3 flex justify-center">
                    <button className="px-5 py-[6px] hover:bg-blue-200 bg-blue-100 text-white rounded font-light">
                      {isLoading ? (
                        <ClipLoader size={25} color="white" />
                      ) : (
                        t('Save-changes', "Sauvegarder les changements")
                      )}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>

      <div className="sm:flex sm:space-x-4">
        
        {/* CHANGE PASSWORD */}
        <div className="sm:w-1/2">
          <Formik
            initialValues={ChangePasswordInitialValues}
            enableReinitialize={true}
            validationSchema={ChangePasswordSchema}
            onSubmit={async (values,{resetForm}) => {
              try{
              const data={
                old_password:values.oldPassword,
                new_password:values.newPassword,
                confirm_password:values.confirmNewPassword,
              }
              if(values.newPassword!==values.confirmNewPassword){
                toast.error("New Password and Confirm New Password Doesn't Match")
                return;
              }
              const response=await updatePassword(data);
              if(response?.data?.status===200){
                toast.success(response?.data?.message)
                resetForm()
              }else{
                toast.error(response?.error?.data?.detail)
              }
            }catch(error){
              toast.error("Error Occur While Changing Password")
            }
            }}
          >
            {(props) => (
              <form onSubmit={props.handleSubmit} className="w-full p-6 mb-5 mx-auto flex flex-col bg-white shadow-default rounded-2xl">
                <h2 className="mb-4">{t("Change-password", "Changer le mot de passe")}</h2>

                <div className="grid gap-5">
                  
                  <div className="w-full">
                    <InputProfile
                      formikProps={props}
                      type="password"
                      text={t("enter-ancient-password", "Entrez votre ancien mot de passe")}
                      name="oldPassword"
                      placeholder={t("enter-ancient-password", "Entrez votre ancien mot de passe")}
                    />
                  </div>

                  <div className="w-full">
                    <InputProfile
                      formikProps={props}
                      type="password"
                      text={t("enter-new-password", "Entrez votre nouveau mot de passe")}
                      name="newPassword"
                      placeholder={t("enter-new-password", "Entrez votre nouveau mot de passe")}
                    />
                  </div>

                  <div className="w-full">
                    <InputProfile
                      formikProps={props}
                      type="password"
                      text={t("confirm-new-password", "Confirmez votre nouveau mot de passe")}
                      name="confirmNewPassword"
                      placeholder={t("confirm-new-password", "Confirmez votre nouveau mot de passe")}
                    />
                  </div>
                  
                  <div className="flex justify-center">
                    <button className="px-5 py-[6px] hover:bg-blue-200 *: bg-blue-100 text-white rounded font-light">
                    {passwordIsLoading ? (
                      <ClipLoader size={25} color="white" />
                    ) : (
                      t('Change-password','Changer le mot de passe')
                    )}
                    </button>
                  </div>
                  
                </div>
              </form>
            )}
          </Formik>
        </div>
        
        {/* CHANGE EMAIL */}
        <div className="sm:w-1/2">
          <Formik
            initialValues={ChangeEmailInitialValues}
            enableReinitialize={true}
            validationSchema={ChangeEmailSchema}
            onSubmit={async (values) => {
              const response=await updateEmail(values);
              if(response?.data?.status===200){
                toast.success(response?.data?.message)
              } else {
                toast.error(response?.error?.data?.detail)
              }
      
            }}
          >
            {(props) => (
              <form onSubmit={props.handleSubmit} className="w-full p-6 mb-5 mx-auto flex flex-col bg-white shadow-default rounded-2xl">
                <h2 className="mb-4">{t("Change-email-adress", "Changer l'adresse email")}</h2>
                <div className="grid gap-5">
    
                  <div className="w-full">
                    <InputProfile
                      formikProps={props}
                      type="text"
                      text={t("enter-old-email", "Entrez votre ancien email")}
                      name="old_email"
                      placeholder={t("enter-old-email", "Entrez votre ancien email")}
                    />
                  </div>
    
                  <div className="w-full">
                    <InputProfile
                      formikProps={props}
                      type="text"
                      text={t("enter-new-email", "Entrez votre nouvel email")}
                      name="new_email"
                      placeholder={t("enter-new-email", "Entrez votre nouvel email")}
                    />
                  </div>
    
                  <div className="flex justify-center">
                    <button className="px-5 py-[6px] hover:bg-blue-200 *: bg-blue-100 text-white rounded font-light">
                      {emailIsLoading ? (
                        <ClipLoader size={25} color="white" />
                      ) : (
                        t('Change-email',"Changer l'email")
                      )}
                    </button>
                  </div>
                  
                </div>
              </form>
            )}
          </Formik>
        </div>
      
      </div>
    </div>
  );
};

export default Account;
